import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@ais-ui/ui-core';
import { UseFormReturn } from 'react-hook-form';

import { ShapeSelect } from '../../../Shapes/ShapeSelects/ShapeSelect';

import { ReportShapeItem, ShapeStructure, ShapeType } from '../../reportsTypes';
import { ShapeMultiSelect } from '../../../Shapes/ShapeSelects/ShapeMultiSelect';
import { ReportCloneValues } from './CloneReport';
import React, { useMemo } from 'react';
import { PortMultiSelect } from '../../../Shapes/ShapeSelects/PortMultiSelect';

interface ShapeStepProps {
  form: UseFormReturn<ReportCloneValues>;
  shapeType: ShapeType;
  setShapeType: (shapeType: ShapeType) => void;
  shapeFields: ShapeStructure;
}

interface ShapeFormProps {
  form: UseFormReturn<ReportCloneValues>;
  shapeItems: ReportShapeItem[];
}

interface SingleContentProps {
  form: UseFormReturn<ReportCloneValues>;
  shapeFields: ShapeStructure;
}

interface TabbedContentProps {
  form: UseFormReturn<ReportCloneValues>;
  shapeFields: ShapeStructure;
  shapeType: ShapeType;
  setShapeType: (shapeType: ShapeType) => void;
}

const ShapeForm = ({ form, shapeItems }: ShapeFormProps) => {
  return (
    <>
      {shapeItems.map((shapeItem) => (
        <FormField
          key={shapeItem.name}
          control={form.control}
          name={shapeItem.name}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{shapeItem.label}: *</FormLabel>
              <FormControl onChange={() => form.clearErrors()}>
                <ShapeSelect
                  value={field.value}
                  onChange={field.onChange}
                  allowedGeometryTypes={shapeItem.allowedGeometryTypes}
                ></ShapeSelect>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ))}
    </>
  );
};

const MultiShapeForm = ({ form, shapeItems }: ShapeFormProps) => {
  return (
    <>
      {shapeItems.map((shapeItem) => (
        <FormField
          key={shapeItem.name}
          control={form.control}
          name={shapeItem.name}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{shapeItem.label}: *</FormLabel>
              <FormControl onChange={() => form.clearErrors()}>
                <ShapeMultiSelect
                  value={field.value}
                  onChange={field.onChange}
                  allowedGeometryTypes={shapeItem.allowedGeometryTypes}
                ></ShapeMultiSelect>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ))}
    </>
  );
};

const PortForm = ({ form, shapeItems }: ShapeFormProps) => {
  return (
    <>
      {shapeItems.map((shapeItem) => (
        <FormField
          key={shapeItem.name}
          control={form.control}
          name={shapeItem.name}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{shapeItem.label}: *</FormLabel>
              <FormControl onChange={() => form.clearErrors()}>
                <PortMultiSelect
                  value={Array.isArray(field.value) ? field.value : []}
                  onChange={field.onChange}
                ></PortMultiSelect>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ))}
    </>
  );
};

const WorldForm = ({ form, shapeItems }: ShapeFormProps) => {
  return (
    <>
      {shapeItems.map((shapeItem) => (
        <FormField
          key={shapeItem.name}
          control={form.control}
          name={shapeItem.name}
          render={({ field }) => (
            <FormItem className="flex flex-col space-y-4">
              <FormLabel>{shapeItem.label}: *</FormLabel>
              <FormControl onChange={() => form.clearErrors()}>
                <Switch disabled={true} checked={field.value} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ))}
    </>
  );
};

const SingleContent = ({ form, shapeFields }: SingleContentProps) => {
  if (
    Object.prototype.hasOwnProperty.call(shapeFields, 'shape') &&
    shapeFields.shape !== undefined
  ) {
    const shapeItems: ReportShapeItem[] = shapeFields.shape;
    return <ShapeForm form={form} shapeItems={shapeItems} />;
  } else if (
    Object.prototype.hasOwnProperty.call(shapeFields, 'multiShape') &&
    shapeFields.multiShape !== undefined
  ) {
    const shapeItems: ReportShapeItem[] = shapeFields.multiShape;
    return (
      <MultiShapeForm form={form} shapeItems={shapeItems}></MultiShapeForm>
    );
  } else if (
    Object.prototype.hasOwnProperty.call(shapeFields, 'worldShape') &&
    shapeFields.worldShape !== undefined
  ) {
    return;
  } else if (
    Object.prototype.hasOwnProperty.call(shapeFields, 'ports') &&
    shapeFields.ports !== undefined
  ) {
    const shapeItems: ReportShapeItem[] = shapeFields.ports;
    return <PortForm form={form} shapeItems={shapeItems}></PortForm>;
  }
};

const TabbedContent = ({
  form,
  shapeFields,
  shapeType,
  setShapeType,
}: TabbedContentProps) => {
  const shapeItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(shapeFields, 'shape')) {
      return shapeFields.shape;
    }
    return null; // or a default value
  }, [shapeFields]);

  const multiShapeItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(shapeFields, 'multiShape')) {
      return shapeFields.multiShape;
    }
    return null; // or a default value
  }, [shapeFields]);

  const portItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(shapeFields, 'ports')) {
      return shapeFields.ports;
    }
    return null; // or a default value
  }, [shapeFields]);

  const worldShapeItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(shapeFields, 'worldShape')) {
      return shapeFields.worldShape;
    }
    return null; // or a default value
  }, [shapeFields]);

  return (
    <Tabs
      value={shapeType}
      onValueChange={setShapeType as (value: string) => void}
      defaultValue="shape"
      className="w-full space-y-4 pt-4 h-full flex flex-col"
    >
      <TabsList className="w-fit">
        {shapeItems && <TabsTrigger value="shape">Shape</TabsTrigger>}
        {multiShapeItems && (
          <TabsTrigger value="multiShape">Multi shape</TabsTrigger>
        )}
        {portItems && <TabsTrigger value="ports">Port</TabsTrigger>}
        {worldShapeItems && <TabsTrigger value="worldShape">World</TabsTrigger>}
      </TabsList>
      {shapeItems && shapeType === 'shape' && (
        <ShapeForm form={form} shapeItems={shapeItems} />
      )}
      {multiShapeItems && shapeType === 'multiShape' && (
        <MultiShapeForm form={form} shapeItems={multiShapeItems} />
      )}
      {portItems && shapeType === 'ports' && (
        <PortForm form={form} shapeItems={portItems} />
      )}
      {worldShapeItems && shapeType === 'worldShape' && (
        <WorldForm form={form} shapeItems={worldShapeItems} />
      )}
    </Tabs>
  );
};

export const ShapeFormField = ({
  form,
  shapeFields,
  shapeType,
  setShapeType,
}: ShapeStepProps) => {
  return (
    <>
      {Object.keys(shapeFields).length === 1 && (
        <SingleContent form={form} shapeFields={shapeFields}></SingleContent>
      )}
      {Object.keys(shapeFields).length > 1 && (
        <TabbedContent
          form={form}
          shapeFields={shapeFields}
          shapeType={shapeType}
          setShapeType={setShapeType}
        ></TabbedContent>
      )}
    </>
  );
};
